/**
 * 日期范围自然语言解析器
 *
 * 让用户在 date_range 题型里直接输入"上周/本月/最近30天/3月1日到3月15日"
 * 而不是手点日历 picker。
 *
 * 设计：纯函数 + 无外部依赖。覆盖中文常见 80% 表达；命中失败返回 null，
 * UI 层降级回 picker，永远不阻塞用户。
 */

export interface DateRange {
  start: string // YYYY-MM-DD
  end: string // YYYY-MM-DD
}

const fmt = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const startOfDay = (d: Date): Date => {
  const r = new Date(d)
  r.setHours(0, 0, 0, 0)
  return r
}

const addDays = (d: Date, n: number): Date => {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

const addMonths = (d: Date, n: number): Date => {
  const r = new Date(d)
  r.setMonth(r.getMonth() + n)
  return r
}

/** 周一为第一天，符合中文语境的"本周" */
const startOfWeek = (d: Date): Date => {
  const day = d.getDay() // 0=Sun..6=Sat
  const offset = day === 0 ? 6 : day - 1
  return startOfDay(addDays(d, -offset))
}

const startOfMonth = (d: Date): Date => {
  const r = startOfDay(d)
  r.setDate(1)
  return r
}

const endOfMonth = (d: Date): Date => {
  const r = startOfDay(d)
  r.setMonth(r.getMonth() + 1, 0) // 下月 0 号 = 本月末
  return r
}

const endOfYear = (d: Date): Date => {
  const r = startOfDay(d)
  r.setMonth(11, 31)
  return r
}

const startOfYear = (d: Date): Date => {
  const r = startOfDay(d)
  r.setMonth(0, 1)
  return r
}

const range = (start: Date, end: Date): DateRange => ({
  start: fmt(start),
  end: fmt(end)
})

/** 把汉字数字转成 Arabic（一→1，二→2，... 十→10，二十→20，等等）。
 *  超出 99 不处理，返回 NaN（自然语言里"最近一百天"这种少见，不值得复杂化）
 */
const cnNumToInt = (s: string): number => {
  if (!s) return NaN
  if (/^\d+$/.test(s)) return parseInt(s, 10)
  const map: Record<string, number> = {
    一: 1,
    二: 2,
    两: 2,
    三: 3,
    四: 4,
    五: 5,
    六: 6,
    七: 7,
    八: 8,
    九: 9,
    十: 10
  }
  if (s.length === 1 && map[s] !== undefined) return map[s]
  // 二十、三十、四十...
  if (s.length === 2 && s[1] === '十' && map[s[0]] !== undefined) {
    return map[s[0]] * 10
  }
  // 十一、十二、... 十九
  if (s.length === 2 && s[0] === '十' && map[s[1]] !== undefined) {
    return 10 + map[s[1]]
  }
  // 二十一、二十三、... 九十九
  if (s.length === 3 && s[1] === '十' && map[s[0]] && map[s[2]]) {
    return map[s[0]] * 10 + map[s[2]]
  }
  return NaN
}

/** 解析 "M月D日"、"M-D"、"M/D"、"YYYY-MM-DD"、"YYYY/MM/DD" 等单点日期。
 *  返回 null 表示没匹配上。
 */
const parseSingleDate = (s: string, refYear: number): Date | null => {
  const trimmed = s.trim()
  // YYYY-MM-DD 或 YYYY/MM/DD
  const ymd = trimmed.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/)
  if (ymd) {
    const [, y, m, d] = ymd
    return startOfDay(new Date(parseInt(y), parseInt(m) - 1, parseInt(d)))
  }
  // M月D日 / M月D号
  const cn = trimmed.match(/^(\d{1,2})月(\d{1,2})[日号]?$/)
  if (cn) {
    return startOfDay(new Date(refYear, parseInt(cn[1]) - 1, parseInt(cn[2])))
  }
  // M-D / M/D（不带年）
  const md = trimmed.match(/^(\d{1,2})[-/.](\d{1,2})$/)
  if (md) {
    return startOfDay(new Date(refYear, parseInt(md[1]) - 1, parseInt(md[2])))
  }
  return null
}

/** 主入口：把自然语言串解析成 DateRange，失败返回 null。
 *  now 默认为当前时间，单测里可注入。
 */
export function parseDateRange(
  input: string,
  now: Date = new Date()
): DateRange | null {
  if (!input) return null
  // 把"~ / ～ / — / –"统一成 ~ 作为范围分隔；不能把 "-" 也归一，否则
  // ISO 格式 2026-03-01 里的连字符会被破坏。
  const text = input
    .trim()
    .replace(/\s+/g, '')
    .replace(/[~～—–]+/g, '~')

  if (!text) return null

  const today = startOfDay(now)
  const refYear = today.getFullYear()

  // ---- 单点表达（输入只有一个日期 → start = end）----
  if (text === '今天' || text === '今日') {
    return range(today, today)
  }
  if (text === '昨天' || text === '昨日') {
    const y = addDays(today, -1)
    return range(y, y)
  }
  if (text === '前天') {
    const y = addDays(today, -2)
    return range(y, y)
  }
  if (text === '明天') {
    const y = addDays(today, 1)
    return range(y, y)
  }

  // ---- 周 ----
  if (text === '本周' || text === '这周') {
    const s = startOfWeek(today)
    return range(s, addDays(s, 6))
  }
  if (text === '上周') {
    const s = addDays(startOfWeek(today), -7)
    return range(s, addDays(s, 6))
  }
  if (text === '上上周') {
    const s = addDays(startOfWeek(today), -14)
    return range(s, addDays(s, 6))
  }
  if (text === '下周') {
    const s = addDays(startOfWeek(today), 7)
    return range(s, addDays(s, 6))
  }

  // ---- 月 ----
  if (text === '本月' || text === '这个月') {
    return range(startOfMonth(today), endOfMonth(today))
  }
  if (text === '上月' || text === '上个月') {
    const ref = addMonths(today, -1)
    return range(startOfMonth(ref), endOfMonth(ref))
  }
  if (text === '下月' || text === '下个月') {
    const ref = addMonths(today, 1)
    return range(startOfMonth(ref), endOfMonth(ref))
  }

  // ---- 年 ----
  if (text === '今年' || text === '本年') {
    return range(startOfYear(today), endOfYear(today))
  }
  if (text === '去年') {
    const ref = new Date(today.getFullYear() - 1, 0, 1)
    return range(startOfYear(ref), endOfYear(ref))
  }

  // ---- 最近 N 天/周/月 ----
  // 支持: "最近30天", "近30天", "过去30天", "最近一周", "近半年"
  const recentMatch = text.match(
    /^(?:最近|近|过去)([0-9一二两三四五六七八九十]+|半)(天|日|周|星期|个?月|年)$/
  )
  if (recentMatch) {
    const numStr = recentMatch[1]
    const unit = recentMatch[2]
    const n = numStr === '半' ? 0.5 : cnNumToInt(numStr)
    if (!isNaN(n) && n > 0) {
      let days = 0
      if (unit === '天' || unit === '日') {
        days = Math.max(1, Math.round(n))
      } else if (unit === '周' || unit === '星期') {
        days = Math.max(1, Math.round(n * 7))
      } else if (unit === '月' || unit === '个月') {
        // 半年/几个月用月份回溯，更符合直觉
        const start = addMonths(today, -Math.round(n * (n === 0.5 ? 12 : 1)))
        return range(startOfDay(start), today)
      } else if (unit === '年') {
        const yearsBack = n === 0.5 ? 0 : Math.round(n)
        if (n === 0.5) {
          return range(addMonths(today, -6), today)
        }
        const start = new Date(today)
        start.setFullYear(start.getFullYear() - yearsBack)
        return range(startOfDay(start), today)
      }
      if (days > 0) {
        return range(addDays(today, -(days - 1)), today)
      }
    }
  }

  // ---- 范围表达：A 到 B / A~B / A 至 B ----
  const rangeMatch = text.split(/到|至|~/)
  if (rangeMatch.length === 2) {
    const a = parseSingleDate(rangeMatch[0], refYear)
    const b = parseSingleDate(rangeMatch[1], refYear)
    if (a && b) {
      return a <= b ? range(a, b) : range(b, a)
    }
  }

  // ---- 单点日期（"3月15日" → 当天）----
  const singlePoint = parseSingleDate(text, refYear)
  if (singlePoint) {
    return range(singlePoint, singlePoint)
  }

  return null
}

/**
 * 英文 token 到中文 label 的映射 — 让后端用 `last_month` / `this_week` 这种
 * 标准 token 也能命中 parseDateRange.
 *
 * 后端在 user_ask 的 default 字段(或 single_choice option.value)常用英文 token,
 * 前端选项 label 是中文(parseDateRange 直接吃中文).当 option 没 label 或者只用
 * value 标识时,走这个 mapper 转一次再 parse.
 */
const EN_TOKEN_TO_CN_LABEL: Record<string, string> = {
  this_week: '本周',
  last_week: '上周',
  this_month: '本月',
  last_month: '上月',
  today: '今天',
  yesterday: '昨天',
  tomorrow: '明天',
  this_year: '今年',
  last_year: '去年'
}

export function tokenToCnLabel(token: string): string | null {
  if (!token) return null
  return EN_TOKEN_TO_CN_LABEL[token.toLowerCase()] || null
}

/**
 * 根据 user_ask single_choice question 的 default(及对应 option)推断
 * 默认日期范围.用户切到「自定义」(expands=date_range)时,把这个范围预填到
 * 日期 picker — 比"留空让用户从头填"更友好.
 *
 * 推断优先级:
 *   1. default 对应 option 的 label 是中文表达(本周/上月/...)→ parseDateRange 解析
 *   2. default 是英文 token(last_month / this_week / ...)→ token mapper 翻成
 *      中文再 parseDateRange
 *   3. default 本身是中文表达(如 default: "上月" 直接当 label)→ parseDateRange
 *   4. 都不命中 → 返回 null,UI 不预填
 */
export function deriveDateRangeDefault(
  defaultValue: unknown,
  options?: Array<{ value: string; label?: string }>,
  now: Date = new Date()
): DateRange | null {
  if (typeof defaultValue !== 'string' || !defaultValue) return null
  const defaultOpt = (options || []).find((o) => o.value === defaultValue)
  // 1 + 3: 取 label(常是中文)直接 parseDateRange
  const fromLabel = defaultOpt?.label
    ? parseDateRange(defaultOpt.label, now)
    : null
  if (fromLabel) return fromLabel
  // 2: 英文 token → 中文 label → parseDateRange
  const cn = tokenToCnLabel(defaultValue)
  if (cn) {
    const fromToken = parseDateRange(cn, now)
    if (fromToken) return fromToken
  }
  // 3 兜底: default 本身就是中文表达("上月")
  return parseDateRange(defaultValue, now)
}
